import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { toast } from "sonner";
import { api, errText } from "@/lib/devadminApi";

const NEW = () => ({ id: `new-${Date.now()}`, _new: true, name: "", budget_paise: null });

export function ExpenseCategories({ eventId }) {
  const [rows, setRows] = useState(null);
  const [orig, setOrig] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/events/${eventId}/expense-categories`).then((r) => {
    setRows(r.data);
    setOrig(Object.fromEntries(r.data.map((x) => [x.id, x])));
  }).catch((e) => toast.error(errText(e)));

  useEffect(() => { setRows(null); load(); }, [eventId]); // eslint-disable-line

  const upd = (i, k, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  const del = async (i) => {
    const r = rows[i];
    if (r._new) return setRows(rows.filter((_, idx) => idx !== i));
    if (!window.confirm(`Delete category "${r.name}"? Historical entries keep their category label.`)) return;
    try { await api.delete(`/expense-categories/${r.id}`); toast.success(`${r.name} removed`); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  const save = async () => {
    setBusy(true);
    const ops = rows.map((r) => {
      const name = (r.name || "").trim();
      if (!name) return null;
      const rupees = r._budget_rupees;
      const budget_paise = rupees !== undefined
        ? (rupees === "" || rupees === null ? null : Math.round(Number(rupees) * 100))
        : (r.budget_paise ?? null);
      const body = { name, budget_paise };
      if (r._new) return api.post(`/events/${eventId}/expense-categories`, body);
      const o = orig[r.id];
      const diff = Object.fromEntries(Object.entries(body).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(o?.[k])));
      return Object.keys(diff).length ? api.patch(`/expense-categories/${r.id}`, diff) : null;
    }).filter(Boolean);
    if (!ops.length) { setBusy(false); return toast.info("No changes"); }
    const res = await Promise.allSettled(ops);
    const failed = res.filter((x) => x.status === "rejected");
    failed.length ? toast.error(errText(failed[0].reason)) : toast.success(`Expense categories saved · ${ops.length} change${ops.length > 1 ? "s" : ""}`);
    setBusy(false); load();
  };

  if (!rows) return <div className="mono-label" data-testid="exp-cats-loading">Loading expense categories…</div>;

  return (
    <Panel className="p-6">
      <div className="flex items-center justify-between">
        <MonoLabel>Expense Categories · budgets are optional</MonoLabel>
        <button data-testid="exp-cats-add-btn" onClick={() => setRows([...rows, NEW()])} className="btn-ghost">
          <Plus size={14} /> Add category
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left mono-label">
              <th className="py-3 pr-3">Name</th>
              <th className="py-3 pr-3">Budget (₹) · optional</th>
              <th className="py-3 pr-3">Stored (paise)</th>
              <th className="py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4} className="py-8 mono-label" style={{ color: "var(--taupe)" }}>No categories yet · click "Add category"</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={r.id} className="hairline-t" data-testid={`exp-cat-row-${i}`}>
                <td className="py-2 pr-3">
                  <input data-testid={`exp-cat-name-${i}`} className="input-x !py-2" value={r.name || ""} placeholder="e.g. venue" onChange={(e) => upd(i, "name", e.target.value)} />
                </td>
                <td className="py-2 pr-3">
                  <input
                    data-testid={`exp-cat-budget-${i}`}
                    type="number" step="0.01" min="0" placeholder="—"
                    className="input-x !py-2 !w-40"
                    value={r._budget_rupees !== undefined ? r._budget_rupees : (r.budget_paise != null ? (r.budget_paise / 100).toString() : "")}
                    onChange={(e) => upd(i, "_budget_rupees", e.target.value)}
                  />
                </td>
                <td className="py-2 pr-3 mono-label" style={{ color: "var(--taupe)" }}>
                  {r._budget_rupees !== undefined
                    ? (r._budget_rupees === "" ? "—" : Math.round(Number(r._budget_rupees) * 100).toLocaleString())
                    : (r.budget_paise != null ? r.budget_paise.toLocaleString() : "—")}
                </td>
                <td className="py-2 text-right">
                  <button data-testid={`exp-cat-del-${i}`} onClick={() => del(i)} className="p-2 hover:text-[var(--red)]"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button data-testid="exp-cats-save-btn" className="btn-red mt-5" disabled={busy} onClick={save}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save categories
      </button>
      <div className="mono-label mt-4" style={{ color: "var(--muted)" }}>
        All amounts stored in paise (integer). Budgets are optional; the /admin/statistics summary uses them to render "used %".
      </div>
    </Panel>
  );
}
